import _ from "lodash";
import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import DebounceInput from "react-debounce-input";
import { toast } from "react-toastify";
import { Alert as Alert2, Button, ButtonGroup, Card, CardBody, Col, Input, Row, Table} from "reactstrap";
import {
   CustomerTypeahead, FilterDateTime, FilterMultiSelect, FilterSelect,
   ItemTypeahead, MostSold, Pager, ServiceThreadSpinner, SortHeader
} from ".";
import { DesktopContainer } from "../layouts";
import { api, constants, dateHelpers, filterHelpers, helpers, UserContext, } from "../utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const thisYear = moment().year();
const LastMonth = moment().add(-1, "month").month() + 1;
const LastDayOfMonth = moment().add(-1, "month").daysInMonth();
const lastYear = thisYear - 1;
const endDate = `${LastMonth}/${LastDayOfMonth}/${thisYear}`;
const startDate = `${LastMonth}/01/${thisYear}`;
const prevEndDate = `${LastMonth}/${LastDayOfMonth}/${thisYear - 1}`;
const prevStartDate = `${LastMonth}/01/${lastYear}`;

export default function SalesReport(props) {
   const { currentUser } = useContext(UserContext);
   const [classList, setClassList] = useState([]);
   const [customer, setCustomer] = useState(null);
   const [data, setData] = useState(null);
   const [downloading, setDownloading] = useState(false);
   const [footerData, setFooterData] = useState(null);
   const [initialized, setInitialized] = useState(false);
   const [loading, setLoading] = useState(false);
   const [marginFrom, setMarginFrom] = useState(null);
   const [marginTo, setMarginTo] = useState(null);
   const [message, setMessage] = useState(null);
   const [page, setPage] = useState(1);
   const [pageSize, setPageSize] = useState(25);
   const [salespeopleList, setSalespeopleList] = useState(null);
   const [showMostSoldReporting, setShowMostSoldReporting] = useState(false);
   const [showSalesReporting, setShowSalesReporting] = useState(true);
   const [sortDirection, setSortDirection] = useState("desc");
   const [sortField, setSortField] = useState("CurrentSales");
   const [totalPages, setTotalPages] = useState();
   const [filters, setFilters] = useState([
      { filterName: "customerId", value: "" },
      { filterName: "itemNumber", value: "" },
      { filterName: "classList", value: [] },
      { filterName: "salespersonId", value: "" },
      { filterName: "showSalesOnly", value: false },
      { filterName: "startDate", value: startDate },
      { filterName: "endDate", value: endDate },
      { filterName: "previousStartDate", value: prevStartDate },
      { filterName: "previousEndDate", value: prevEndDate },
      { filterName: "includeItemBreakdown", value: false },
   ]);

   useEffect(() => {
      if (initialized) return;
      if (currentUser && !initialized) {
         if (currentUser.customerFilter) {
            currentUser.setCustomerFilter(null);
         }
      }

      let apiCalls = [];
      apiCalls.push(
         api
            .fetch(`Reference/GetSalespeopleList`)
            .then((response) => {
               return {
                  salespeople: response.data.salespeople,
               };
            })
            .catch(api.catchHandler)
      );
      apiCalls.push(
         api
            .fetch(`Reference/GetItemClasses`)
            .then((response) => {
               return {
                  itemClasses: _.map(response.data.itemClasses, (x) => {
                     return {
                        value: x.itemClass,
                        label: x.itemClassWithDesc,
                     };
                  }),
               };
            })
            .catch(api.catchHandler)
      );
      Promise.all(apiCalls)
         .then((arrayResults) => {
            let aggResults = {};
            _.each(arrayResults, (x) => Object.assign(aggResults, x));
            setSalespeopleList(
               _.chain(aggResults.salespeople)
                  .map((sp) => {
                     return {
                        value: sp.salespersonId,
                        label: sp.salespersonText,
                     };
                  })
                  .sortBy((x) => {
                     return x.label;
                  })
                  .unshift({
                     value: "",
                     label: "--- All ---",
                  })
                  .value()
            );
            setClassList(aggResults.itemClasses);
         })
         .catch((error) =>
            setMessage({
               flavor: "alert-danger",
               text: "There was an error loading sales people data",
            })
         )
         .finally(() => setInitialized(true));
   }, [initialized, downloading, currentUser]);

   useEffect(() => {
      if (data === null) return;
      runQuery();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [page, pageSize]);

   useEffect(() => {
      if (_.isEmpty(classList)) return;
      runQuery();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [sortField, sortDirection]);

   useEffect(() => {
      if (!data) return;
      setPage(1);
      runQuery();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [filterHelpers.getValue(filters, "includeItemBreakdown")]);

   function createPayload() {
      //MarginFrom
      let payload = {
         SortField: sortField,
         SortDirection: sortDirection,
         marginFrom: marginFrom,
         marginTo: marginTo,
         PageNumber: page,
         PageSize: pageSize
      };
      _.map(filters, (filter) => {
         payload[filter.filterName] = filter.value;
      });
      if (_.isEmpty(payload.classList))
         payload.classList = generateClassListString();
      payload.startDate = dateHelpers.formatDateForServer(
         new Date(payload.startDate)
      );
      payload.endDate = dateHelpers.formatDateForServer(
         new Date(payload.endDate)
      );
      payload.previousStartDate = dateHelpers.formatDateForServer(
         new Date(payload.previousStartDate)
      );
      payload.previousEndDate = dateHelpers.formatDateForServer(
         new Date(payload.previousEndDate)
      );
      if (payload.selectedItem) {
         payload.itemNumber = payload.selectedItem.itemNumber;
         delete payload.selectedItem;
      }
      return payload;
   }

   function currentDate() {
      let date = new Date();
      let month = date.getMonth() + 1;
      let day = date.getDate();
      let year = date.getFullYear();
      return `${month}-${day}-${year}`;
   }

   function exportXlsx() {
      setDownloading(true);

      let payload = createPayload();
      let url;
      payload.includeItemBreakdown
         ? (url = "Customer/ExportTopCustomersWithItemBreakdownXLSX")
         : (url = "Customer/ExportTopCustomersXLSX");

      api
         .postGetBlob(url, payload)
         .then((response) => {
            if (response.data.success === false) {
               if (_.isString(response?.data?.message)) {
                  toast.error(response?.data?.message);
               }
               return;
            }
            if (response.data) {
               const XLSX_FILENAME = "SalesReport.xlsx";
               helpers.downloadExport(
                  response.data,
                  XLSX_FILENAME,
                  constants.MIME_TYPES.xlsx
               );
            } else {
               toast.error(response.message || "Error downloading report");
            }
         })
         .catch(api.catchHandler)
         .finally(() => setDownloading(false));
   }

   function generateClassListString() {
      let stringList = [];
      stringList = classList ? _.map(classList, (x) => x.value) : [];
      return stringList;
   }

   function handleSetPreviousYear() {
      // moment of a moment to create a copy
      let start = moment(filterHelpers.getValue(filters, "startDate"));
      let end = moment(filterHelpers.getValue(filters, "endDate"));

      // new filter objects
      let newPrevStartFilter = {
         filterName: "previousStartDate",
         value: start.subtract(1, "years"),
         filteredDisplay: `undefined ${start} 12:00AM`
      };
      let newPrevEndFilter = {
         filterName: "previousEndDate",
         value: end.subtract(1, "years"),
         filteredDisplay: `undefined ${end} 12:00AM`
      }

      // create filter array copy so we can set both filters at once
      let filterCopy = _.slice(filters);
      let indexOfStart = _.findIndex(filterCopy, x => x.filterName === "previousStartDate");
      let indexOfEnd = _.findIndex(filterCopy, x => x.filterName === "previousEndDate");
      filterCopy[indexOfStart] = newPrevStartFilter;
      filterCopy[indexOfEnd] = newPrevEndFilter;

      setFilters(filterCopy);
   }

   function onDownloadSalesAnalysisReport() {
      setDownloading(true);
      api
         .postDownloadBinary(
            "Report/SalesAnalysisReport",
            createPayload(),
            constants.MIME_TYPES.pdf,
            `SalesAnalysis_${currentDate()}.pdf`,
            "There was an error downloading the Sales report pdf."
         )
         .then((res) => {
            if (res?.data === false) {
               toast.error(res.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setDownloading(false));
   }

   function onFilterChange(changedFilter) {
      const DATE_FILTER_NAMES = [
         "startDate",
         "endDate",
         "previousStartDate",
         "previousEndDate",
      ];
      if (DATE_FILTER_NAMES.includes(changedFilter.filterName)) {
         changedFilter.value = moment(changedFilter.value);
      }
      const filterChanges = filterHelpers.getFilters(filters, changedFilter);
      setPage(1);
      setFilters(filterChanges);
   }

   function onPageChange(page) {
      setPage(page);
      setLoading(false);
   }

   function onPageSizeChange(size) {
      setPageSize(size);
      setTotalPages(null);
      onPageChange(1);
   }

   function onSortChange(field, direction) {
      setSortField(field);
      setSortDirection(direction);
   }

   function runQuery() {
      setLoading(true);
      const payload = createPayload();
      let sortby = sortField === 'customerId' ? 'customerNumber' : sortField;
      payload.salesperson = payload?.salespersonId;
      api
         .post(`Sales/LoadSalesReport`, payload)
         .then((response) => {
            if (response.data.success) {
               if (payload.includeItemBreakdown) {
                  const footerData = response.data.message;
                  const sortedData = _.chain(response.data.message.items.items)
                     .orderBy([sortby], [sortDirection])
                     // .drop(pageSize * (page - 1))
                     // .take(pageSize)
                     .value();
                  setData(sortedData);
                  setFooterData(footerData);
                  setTotalPages(response.data.message.totalPages);
               } else {
                  const footerData = response.data.message;
                  const sortedData = _.chain(response.data.message.items.items)
                     .orderBy([sortby], [sortDirection])
                     // .drop(pageSize * (page - 1))
                     // .take(pageSize)
                     .value();
                  setData(sortedData);
                  setFooterData(footerData);
                  setTotalPages(response.data.message.totalPages);
               }
            } else {
               setMessage({
                  flavor: "alert-danger",
                  text: "There was an error loading data: " + response.data.message,
               });
            }
         })
         .catch(api.catchHandler)
         .finally(() => setLoading(false));
   }


   return (
      document.title = `Sales Report`,
      <DesktopContainer screenName="Most Sold and Sales Reporting">
         {message ? (
            <Row className="mt-3">
               <Col>
                  <Alert2 className={message.flavor}>{message.text}</Alert2>
               </Col>
            </Row>
         ) : null}
         <div className="filter-header sticky-top">
            <div className="dashboard-button-container">
               <Button
                  children={"Sales Reporting"}
                  className="filter-button"
                  onClick={() => setShowSalesReporting(!showSalesReporting)}
                  active={showSalesReporting}
               />
               <Button
                  children={"Most Sold"}
                  className="filter-button"
                  onClick={() => setShowMostSoldReporting(!showMostSoldReporting)}
                  active={showMostSoldReporting}
               />
            </div>
         </div>
         {showSalesReporting ? (
            <Card className="dashboard-card">
               <CardBody>
                  <div>
                     <span className="h2 me-2">Sales</span>{" "}
                  </div>
                  <hr />
                  <Row
                     className="justify-content-center"
                     style={{ paddingBottom: "30px" }}
                  >
                     <Col xs={12} lg={9}>
                        <Row className="align-items-center my-1">
                           <Col className="text-end  h5" xs={3}>
                              Item #:
                           </Col>
                           <Col xs={8}>
                              <ItemTypeahead
                                 value={filterHelpers.getValue(filters, "selectedItem")}
                                 isClearable
                                 onChange={(selectedItem) => {
                                    onFilterChange({
                                       filterName: "selectedItem",
                                       value: selectedItem,
                                    });
                                 }}
                              />
                           </Col>
                        </Row>
                        <Row className="align-items-center my-1">
                           <Col className="text-end  h5" xs={3}>
                              Classes:
                           </Col>
                           <Col xs={8}>
                              <FilterMultiSelect
                                 filterName="classList"
                                 options={classList}
                                 values={filterHelpers.getValue(filters, "classList")}
                                 onChangeCallback={(e) => onFilterChange(e)}
                              />
                           </Col>
                        </Row>
                        <Row className="align-items-center my-1">
                           <Col className="text-end  h5" xs={3}>
                              Sales Person:
                           </Col>
                           <Col xs={8}>
                              <FilterSelect
                                 classNamePrefix="serviceThreadSelect"
                                 filterName="salespersonId"
                                 options={salespeopleList}
                                 value={filterHelpers.getValue(filters, "salespersonId")}
                                 onChangeCallback={(e) => onFilterChange(e)}
                                 isClearable
                              />
                           </Col>
                        </Row>
                        {!currentUser?.isAnyCustomerTypeRole && (
                           <Row className="align-items-center my-1">
                              <Col className="text-end  h5" xs={3}>
                                 Customer:
                              </Col>
                              <Col xs={8}>
                                 <CustomerTypeahead
                                    value={customer}
                                    onChange={(c) => {
                                       setCustomer(c);
                                       if (c) {
                                          onFilterChange({
                                             filterName: "customerId",
                                             value: c.value,
                                             filteredDisplay: `Customer: ${c.label}`,
                                          });
                                       } else {
                                          setFilters(
                                             _.reject(
                                                filters,
                                                (x) => x.filterName === "customerId"
                                             )
                                          );
                                       }
                                    }}
                                 />
                              </Col>
                           </Row>
                        )}
                        <Row className="justify-content-center">
                           <Col xs={10}>
                              <Row style={{ paddingTop: "20px" }}>
                                 <Col xs={6}>
                                    <h5
                                       children="Current Sales Period"
                                       className="float-start "
                                    />
                                 </Col>
                              </Row>
                              <Row className="justify-content-center pe-5 my-2">
                                 <Col xs={3}>
                                    <h5 children="Start Date:" className="float-end " />
                                 </Col>
                                 <Col xs={3}>
                                    <FilterDateTime
                                       filterName="startDate"
                                       value={dateHelpers.formatDateForServer(
                                          filterHelpers.getValue(filters, "startDate")
                                       )}
                                       onChangeCallback={(e) => onFilterChange(e)}
                                    />
                                 </Col>
                                 <Col xs={3}>
                                    <h5 children="End Date:" className="float-end " />
                                 </Col>
                                 <Col xs={3}>
                                    <FilterDateTime
                                       filterName="endDate"
                                       value={dateHelpers.formatDateForServer(
                                          filterHelpers.getValue(filters, "endDate")
                                       )}
                                       onChangeCallback={(e) => onFilterChange(e)}
                                    />
                                 </Col>
                              </Row>
                           </Col>
                        </Row>
                        <Row className="justify-content-center">
                           <Col xs={10}>
                              <Row>
                                 <Col xs={6} className="d-flex align-items-center">
                                    <h5
                                       children="Previous Sales Period"
                                       className="float-start me-3"
                                    />
                                    <h6
                                       className="shredLink"
                                       onClick={() => handleSetPreviousYear()}
                                    >
                                       Set Previous Year
                                    </h6>
                                 </Col>
                              </Row>
                              <Row className="justify-content-center pe-5 my-2">
                                 <Col xs={3}>
                                    <h5 children="Start Date:" className="float-end " />
                                 </Col>
                                 <Col xs={3}>
                                    <FilterDateTime
                                       filterName="previousStartDate"
                                       value={dateHelpers.formatDateForServer(
                                          filterHelpers.getValue(filters, "previousStartDate")
                                       )}
                                       onChangeCallback={(e) => onFilterChange(e)}
                                    />
                                 </Col>
                                 <Col xs={3}>
                                    <h5 children="End Date:" className="float-end " />
                                 </Col>
                                 <Col xs={3}>
                                    <FilterDateTime
                                       filterName="previousEndDate"
                                       value={dateHelpers.formatDateForServer(
                                          filterHelpers.getValue(filters, "previousEndDate")
                                       )}
                                       onChangeCallback={(e) => onFilterChange(e)}
                                    />
                                 </Col>
                              </Row>
                              {currentUser?.isPowerUser === true ||
                                 currentUser?.isAdmin === true ? (
                                 <Row style={{ paddingTop: "20px", paddingBottom: "5px" }}>
                                    <Col xs="3">
                                       <h5
                                          children="Margin From:"
                                          className="float-start "
                                       ></h5>
                                    </Col>
                                    <Col xs="2">
                                       <DebounceInput
                                          type="text"
                                          value={marginFrom}
                                          onChange={(e) => {
                                             setMarginFrom(e.target.value);
                                          }}
                                          debounceTimeout={300}
                                          className="float-start form-control"
                                       />
                                    </Col>
                                 </Row>
                              ) : null}
                              {currentUser?.isPowerUser === true ||
                                 currentUser?.isAdmin === true ? (
                                 <Row style={{ paddingTop: "20px", paddingBottom: "5px" }}>
                                    <Col xs="3">
                                       <h5
                                          children="Margin To:"
                                          className="float-start "
                                       ></h5>
                                    </Col>
                                    <Col xs="2">
                                       <DebounceInput
                                          type="text"
                                          value={marginTo}
                                          onChange={(e) => {
                                             setMarginTo(e.target.value);
                                          }}
                                          debounceTimeout={300}
                                          className="float-start form-control"
                                       />
                                    </Col>
                                 </Row>
                              ) : null}
                           </Col>
                        </Row>
                        <Row style={{ paddingTop: "20px", paddingBottom: "20px" }}>
                           <Col xs="1"></Col>
                           <Col xs="3">
                              <h5
                                 children="Include Item Breakdown?"
                                 className="float-end "
                              />
                           </Col>
                           <Col xs="1">
                              <Input
                                 type="checkbox"
                                 value={filterHelpers.getValue(
                                    filters,
                                    "includeItemBreakdown"
                                 )}
                                 onChange={
                                    ({ target }) => {
                                       onFilterChange({
                                          filterName: "includeItemBreakdown",
                                          value: target.checked,
                                       });
                                    }
                                 }
                              />
                              )
                           </Col>
                        </Row>
                        <Row style={{ display: "flex", justifyContent: "center" }}>
                           <Col xs={8} style={{ display: "flex" }}>
                              <Button
                                 title="View Sales"
                                 className="shredButton float-end ms-2"
                                 onClick={runQuery}
                              >
                                 View Sales
                              </Button>
                              <Button
                                 title="Print Sales Analysis Report"
                                 className="shredButton float-end ms-2"
                                 onClick={onDownloadSalesAnalysisReport}
                              >
                                 Print Report
                              </Button>
                           </Col>
                        </Row>
                     </Col>
                  </Row>
                  {data ? (
                     <ServiceThreadSpinner on={loading}>
                        <Row className="align-items-center mb-1">
                           <Col className="transparent-background d-flex">
                              <Pager
                                 callBack={onPageChange}
                                 sizeCallback={onPageSizeChange}
                                 pageNumber={page}
                                 pageSize={pageSize}
                                 totalPages={totalPages}
                                 downloading={downloading}
                                 defaultPageSize={25}
                                 allowAll
                              />
                           </Col>
                           <Col className="transparent-background">
                              <ButtonGroup className="float-end">
                                 <Button
                                    title="Download as .xlsx"
                                    className="shredButtonExcel float-end me-3"
                                    onClick={exportXlsx}
                                    disabled={downloading}
                                 >
                                    <FontAwesomeIcon icon="file-excel" size="lg" />
                                 </Button>
                              </ButtonGroup>
                           </Col>
                        </Row>
                        <Table striped className="data-table">
                           <thead className="table-header">
                              <tr>
                                 <th>
                                    {/*{filterHelpers.getValue(filters, "includeItemBreakdown") === false || null ? (*/}
                                    {/*  <SortHeader*/}
                                    {/*    displayName="Customer Name"*/}
                                    {/*    field="customerName"*/}
                                    {/*    sortDirection={sortDirection}*/}
                                    {/*    sorted={sortField === "customerName"}*/}
                                    {/*    callback={onSortChange}*/}
                                    {/*  />*/}
                                    {/*) : (*/}
                                    <SortHeader
                                       displayName="Customer Name"
                                       field="customerId"
                                       sortDirection={sortDirection}
                                       sorted={sortField === "customerId"}
                                       callback={onSortChange}
                                    />
                                    {/*)}*/}
                                 </th>
                                 {filterHelpers.getValue(filters, "includeItemBreakdown") === false || null ? null : (
                                    <th>
                                       <SortHeader
                                          displayName="Customer Item"
                                          field="customerPartNumber"
                                          sortDirection={sortDirection}
                                          sorted={sortField === "customerPartNumber"}
                                          callback={onSortChange}
                                       />
                                    </th>
                                 )}
                                 {filterHelpers.getValue(filters, "includeItemBreakdown") === false || null ? null : (
                                    <th>
                                       <SortHeader
                                          displayName="Item"
                                          field="itemNumber"
                                          sortDirection={sortDirection}
                                          sorted={sortField === "itemNumber"}
                                          callback={onSortChange}
                                       />
                                    </th>
                                 )}
                                 <th style={{ textAlign: "center" }}>
                                    <SortHeader
                                       displayName="Current Sales"
                                       field="CurrentSales"
                                       sortDirection={sortDirection}
                                       sorted={sortField === "CurrentSales"}
                                       callback={onSortChange}
                                    />
                                 </th>
                                 <th style={{ textAlign: "center" }}>
                                    <SortHeader
                                       displayName="Previous Sales"
                                       field="previousSales"
                                       sortDirection={sortDirection}
                                       sorted={sortField === "previousSales"}
                                       callback={onSortChange}
                                    />
                                 </th>
                                 <th style={{ textAlign: "center" }}>
                                    <SortHeader
                                       displayName="Sales +/-"
                                       field="SalesIncrease"
                                       sortDirection={sortDirection}
                                       sorted={sortField === "SalesIncrease"}
                                       callback={onSortChange}
                                    />
                                 </th>
                                 <th style={{ textAlign: "center" }}>
                                    <SortHeader
                                       displayName="Current Sales (lbs)"
                                       field="currentSalesQty"
                                       sortDirection={sortDirection}
                                       sorted={sortField === "currentSalesQty"}
                                       callback={onSortChange}
                                    />
                                 </th>
                                 <th style={{ textAlign: "center" }}>
                                    <SortHeader
                                       displayName="Previous Sales (lbs)"
                                       field="previousSalesQty"
                                       sortDirection={sortDirection}
                                       sorted={sortField === "previousSalesQty"}
                                       callback={onSortChange}
                                    />
                                 </th>
                                 <th style={{ textAlign: "center" }}>
                                    <SortHeader
                                       displayName="Sales (lbs) +/-"
                                       field="salesQtyIncrease"
                                       sortDirection={sortDirection}
                                       sorted={sortField === "salesQtyIncrease"}
                                       callback={onSortChange}
                                    />
                                 </th>
                                 {currentUser?.isPowerUser || currentUser?.isAdmin ? (
                                    <th style={{ textAlign: "center" }}>
                                       <SortHeader
                                          displayName="Current Margin"
                                          field="currentMargin"
                                          sortDirection={sortDirection}
                                          sorted={sortField === "currentMargin"}
                                          callback={onSortChange}
                                       />
                                    </th>
                                 ) : null}
                                 {currentUser?.isPowerUser || currentUser?.isAdmin ? (
                                    <th style={{ textAlign: "center" }}>
                                       <SortHeader
                                          displayName="Previous Margin"
                                          field="previousMargin"
                                          sortDirection={sortDirection}
                                          sorted={sortField === "previousMargin"}
                                          callback={onSortChange}
                                       />
                                    </th>
                                 ) : null}
                                 <th style={{ textAlign: "center" }}>
                                    <SortHeader
                                       displayName="Margin +/-"
                                       field="marginChange"
                                       sortDirection={sortDirection}
                                       sorted={sortField === "marginChange"}
                                       callback={onSortChange}
                                    />
                                 </th>
                                 <th style={{ textAlign: "center" }}>
                                    <SortHeader
                                       displayName="Sales Person"
                                       field="salespersonId"
                                       sortDirection={sortDirection}
                                       sorted={sortField === "salespersonId"}
                                       callback={onSortChange}
                                    />
                                 </th>
                              </tr>
                           </thead>
                           <tbody>
                              {filterHelpers.getValue(filters, "includeItemBreakdown") === false ?
                                 _.map(data, (datum, index) => {
                                    return (
                                       <tr key={index}>
                                          <td className="data-cell">
                                             <a rel="noreferrer"
                                                href={`/customer-dashboard/${datum.customerNumber}`}
                                                target="_blank"
                                                className="styledAnchor"
                                                style={{ textAlign: "start" }}
                                             >
                                                {datum.custNumName}
                                             </a>
                                          </td>
                                          {filterHelpers.getValue(filters, "includeItemBreakdown") === false || null ? null : (
                                             <td
                                                className="data-cell"
                                                children={datum.customerPartNumber}
                                             />
                                          )}
                                          {filterHelpers.getValue(filters, "includeItemBreakdown") === false || null ? null : (
                                             <td
                                                className="data-cell"
                                                children={datum.itemWithDesc}
                                             />
                                          )}
                                          <td
                                             className="data-cell"
                                             children={helpers.formatCurrency(datum.currentSales)}
                                             style={{ textAlign: "center" }}
                                          />
                                          <td
                                             className="data-cell"
                                             children={helpers.formatCurrency(datum.previousSales)}
                                             style={{ textAlign: "center" }}
                                          />
                                          {datum.currentSales === 0 || datum.previousSales === 0 ? (
                                             <td
                                                className="data-cell"
                                                children={""}
                                                style={{ textAlign: "center" }}
                                             />
                                          ) : (
                                             <td
                                                className="data-cell"
                                                children={helpers.formatPercent(datum.salesIncrease, 2)}
                                                style={
                                                   datum.salesIncrease > 0
                                                      ? { textAlign: "center", color: "green" }
                                                      : { textAlign: "center", color: "red" }
                                                }
                                             />
                                          )}
                                          <td
                                             className="data-cell"
                                             children={datum.currentSalesQty}
                                             style={{ textAlign: "center" }}
                                          />
                                          <td
                                             className="data-cell"
                                             children={datum.previousSalesQty}
                                             style={{ textAlign: "center" }}
                                          />
                                          {datum.currentSalesQty === 0 || datum.previousSalesQty === 0 ? (
                                             <td
                                                className="data-cell"
                                                children={""}
                                                style={{ textAlign: "center" }}
                                             />
                                          ) : (
                                             <td
                                                className="data-cell"
                                                children={helpers.formatPercent(datum.salesQtyIncrease, 2)}
                                                style={
                                                   datum.salesQtyIncrease > 0
                                                      ? { textAlign: "center", color: "green" }
                                                      : { textAlign: "center", color: "red" }
                                                }
                                             />
                                          )}
                                          {currentUser?.isPowerUser || currentUser?.isAdmin ? (
                                             <td
                                                className="data-cell"
                                                children={helpers.formatPercent(datum.currentMargin, 2)}
                                                style={
                                                   datum.currentMargin <= 0
                                                      ? { color: "red", textAlign: "center" }
                                                      : { textAlign: "center" }
                                                }
                                             />
                                          ) : null}
                                          {currentUser?.isPowerUser || currentUser?.isAdmin ? (
                                             <td
                                                className="data-cell"
                                                children={helpers.formatPercent(datum.previousMargin, 2)}
                                                style={{ textAlign: "center" }}
                                             />
                                          ) : null}
                                          <td
                                             className="data-cell"
                                             children={helpers.formatPercent(datum.marginChange, 2)}
                                             style={
                                                datum.marginChange <= 0
                                                   ? { color: "red", textAlign: "center" }
                                                   : { textAlign: "center" }
                                             }
                                          />
                                          {filterHelpers.getValue(filters, "includeItemBreakdown") === false || null ? (
                                             <td
                                                className="data-cell"
                                                children={datum.salespersonId}
                                                style={{ textAlign: "center" }}
                                             />
                                          ) : (
                                             <td
                                                className="data-cell"
                                                children={datum.salesPerson}
                                                style={{ textAlign: "center" }}
                                             />
                                          )}
                                       </tr>
                                    );
                                 })
                                 : _.map(data, (datum, index) => {
                                    return (
                                       <tr key={index}>
                                          <td className="data-cell">
                                             <a rel="noreferrer"
                                                href={`/customer-dashboard/${datum.customerNumber}`}
                                                target="_blank"
                                                className="styledAnchor"
                                                style={{ textAlign: "start" }}
                                             >
                                                {datum.custNumName}
                                             </a>
                                          </td>
                                          {filterHelpers.getValue(filters, "includeItemBreakdown") === false || null ? null : (
                                             <td
                                                className="data-cell"
                                                children={datum.customerPartNumber}
                                             />
                                          )}
                                          {filterHelpers.getValue(filters, "includeItemBreakdown") === false || null ? null : (
                                             <td
                                                className="data-cell"
                                                children={datum.itemWithDesc}
                                             />
                                          )}
                                          <td
                                             className="data-cell"
                                             children={helpers.formatCurrency(datum.currentSales)}
                                             style={{ textAlign: "center" }}
                                          />
                                          <td
                                             className="data-cell"
                                             children={helpers.formatCurrency(datum.previousSales)}
                                             style={{ textAlign: "center" }}
                                          />
                                          {datum.currentSales === 0 || datum.previousSales === 0 ? (
                                             <td
                                                className="data-cell"
                                                children={""}
                                                style={{ textAlign: "center" }}
                                             />
                                          ) : (
                                             <td
                                                className="data-cell"
                                                children={helpers.formatPercent(datum.salesIncrease, 2)}
                                                style={
                                                   datum.salesIncrease > 0
                                                      ? { textAlign: "center", color: "green" }
                                                      : { textAlign: "center", color: "red" }
                                                }
                                             />
                                          )}
                                          <td
                                             className="data-cell"
                                             children={datum.currentSalesQty}
                                             style={{ textAlign: "center" }}
                                          />
                                          <td
                                             className="data-cell"
                                             children={datum.previousSalesQty}
                                             style={{ textAlign: "center" }}
                                          />
                                          {datum.currentSalesQty === 0 || datum.previousSalesQty === 0 ? (
                                             <td
                                                className="data-cell"
                                                children={""}
                                                style={{ textAlign: "center" }}
                                             />
                                          ) : (
                                             <td
                                                className="data-cell"
                                                children={helpers.formatPercent(datum.salesQtyIncrease, 2)}
                                                style={
                                                   datum.salesQtyIncrease > 0
                                                      ? { textAlign: "center", color: "green" }
                                                      : { textAlign: "center", color: "red" }
                                                }
                                             />
                                          )}
                                          {currentUser?.isPowerUser || currentUser?.isAdmin ? (
                                             <td
                                                className="data-cell"
                                                children={helpers.formatPercent(datum.currentMargin, 2)}
                                                style={
                                                   datum.currentMargin <= 0
                                                      ? { color: "red", textAlign: "center" }
                                                      : { textAlign: "center" }
                                                }
                                             />
                                          ) : null}
                                          {currentUser?.isPowerUser || currentUser?.isAdmin ? (
                                             <td
                                                className="data-cell"
                                                children={helpers.formatPercent(datum.previousMargin, 2)}
                                                style={{ textAlign: "center" }}
                                             />
                                          ) : null}
                                          <td
                                             className="data-cell"
                                             children={helpers.formatPercent(datum.marginChange, 2)}
                                             style={
                                                datum.marginChange <= 0
                                                   ? { color: "red", textAlign: "center" }
                                                   : { textAlign: "center" }
                                             }
                                          />
                                          <td
                                             className="data-cell"
                                             children={datum.salesPerson}
                                             style={{ textAlign: "center" }}
                                          />
                                       </tr>
                                    )
                                 })}
                           </tbody>
                           <tfoot className="table-footer">
                              <tr>
                                 <td children={"REPORT TOTALS:"} className={"fw-bold"} />
                                 {filterHelpers.getValue(filters, "includeItemBreakdown") === true || (
                                    <td
                                       className="data-cell"
                                       style={{ textAlign: "center" }}
                                       children={""}
                                    />
                                 ) ? null : (
                                    <td
                                       className="data-cell"
                                       children={footerData?.ytdSalesDisplay}
                                    />
                                 )}
                                 {filterHelpers.getValue(filters, "includeItemBreakdown") === false || null ? null : (
                                    <td className="data-cell" children={""} />
                                 )}
                                 {filterHelpers.getValue(filters, "includeItemBreakdown") === false || null ? null : (
                                    <td className="data-cell" children={data.ytdSales} />
                                 )}
                                 <td
                                    className="data-cell"
                                    style={{ textAlign: "center" }}
                                    children={footerData?.ytdSalesDisplay}
                                 />
                                 <td
                                    className="data-cell"
                                    style={{ textAlign: "center" }}
                                    children={footerData?.previousYTDSalesDisplay}
                                 />
                                 <td
                                    className="data-cell"
                                    style={{ textAlign: "center" }}
                                    children={footerData?.percentChangeSalesDisplay}
                                 />
                                 <td
                                    className="data-cell"
                                    style={{ textAlign: "center" }}
                                    children={footerData?.ytdLbsDisplay}
                                 />
                                 <td
                                    className="data-cell"
                                    style={{ textAlign: "center" }}
                                    children={footerData?.previousYTDLbsDisplay}
                                 />
                                 <td
                                    className="data-cell"
                                    style={{ textAlign: "center" }}
                                    children={footerData?.percentChangeLbsDisplay}
                                 />
                                 {currentUser?.isPowerUser || currentUser?.isAdmin ? (
                                    <td
                                       className="data-cell"
                                       style={{ textAlign: "center" }}
                                       children={footerData?.currentMarginTotalDisplay}
                                    />
                                 ) : null}
                                 {currentUser?.isPowerUser || currentUser?.isAdmin ? (
                                    <td
                                       className="data-cell"
                                       style={{ textAlign: "center" }}
                                       children={footerData?.previousMarginTotalDisplay}
                                    />
                                 ) : null}
                                 <td
                                    className="data-cell"
                                    style={{ textAlign: "center" }}
                                    children={footerData?.marginChangeTotalDisplay}
                                 />

                                 <td className="data-cell" children={""} />
                              </tr>
                           </tfoot>
                        </Table>
                     </ServiceThreadSpinner>
                  ) : (
                     <div className="h5 text-center mt-3"></div>
                  )}
               </CardBody>
            </Card>
         ) : (
            false
         )}
         {showMostSoldReporting ? (
            <Card className="dashboard-card">
               <CardBody>
                  <div>
                     <span className="h2 me-2">Most Sold Report</span>
                  </div>
                  <hr />
                  <MostSold />
               </CardBody>
            </Card>
         ) : (
            false
         )}
      </DesktopContainer>
   );
}
